import { Image as ImageIcon } from '@project-lary/react-material-symbols';
import type React from 'react';
import { type ComponentProps, useEffect, useRef, useState } from 'react';
import { useIntersectionObserver } from 'usehooks-ts';
import { attachSearchParamsToUrl, isExternalUrl } from '../../helper/urlHelper';
import { cn } from '../utils/tailwind-merge';
import { Avatar } from './avatar';

export interface ImageWithFallbackProps {
  src: string | undefined | null;
  fallback?: React.ReactNode;
  hasFallbackBackground?: boolean;
  alt?: string;
  onError?: ComponentProps<'img'>['onError'];
  className?: ComponentProps<'img'>['className'];
  height?: number;
  width?: number;
  quality?: number;
  onLoad?: ComponentProps<'img'>['onLoad'];
}

enum FetchingState {
  Initial = 'initial',
  Loading = 'loading',
  Error = 'error',
  Success = 'success',
}

function ImageWithFallback({
  src,
  fallback,
  alt,
  hasFallbackBackground,
  onError,
  className,
  height,
  width,
  quality = 85,
  onLoad,
  ...props
}: ImageWithFallbackProps) {
  const srcUrl = isExternalUrl(src)
    ? attachSearchParamsToUrl(src, {
        height: height ? height.toString() : undefined,
        width: width ? width.toString() : undefined,
        quality: quality ? quality.toString() : undefined,
      })
    : src;

  const [state, setState] = useState<FetchingState>(FetchingState.Initial);
  const target = useRef<HTMLDivElement>(null);
  const { isIntersecting } = useIntersectionObserver({
    root: document.body,
    rootMargin: '0px',
    threshold: 0,
  });

  useEffect(() => {
    if (!srcUrl) {
      return setState(FetchingState.Error);
    }
    setState(FetchingState.Initial);
  }, [srcUrl]);

  useEffect(() => {
    if (!srcUrl || state === FetchingState.Success || state === FetchingState.Error) {
      return;
    }

    if (!isIntersecting && state === FetchingState.Initial) {
      setState(FetchingState.Loading);
      const img = new Image();
      img.onload = () => setState(FetchingState.Success);
      img.onerror = () => setState(FetchingState.Error);
      img.src = srcUrl;
    }
  }, [isIntersecting, state]);

  return (
    <div ref={target} className="flex size-full items-center justify-center">
      {(state === FetchingState.Loading || state === FetchingState.Error) && (
        <div
          className={cn('flex size-full items-center justify-center', {
            'bg-neutral-3': hasFallbackBackground,
          })}
        >
          <Avatar
            className={cn({
              'animate-pulse duration-400': state === FetchingState.Loading,
            })}
          >
            {fallback || <ImageIcon />}
          </Avatar>
        </div>
      )}
      {state === FetchingState.Success && (
        <img
          src={srcUrl!}
          {...props}
          alt={alt}
          className={`h-auto max-h-full max-w-full rounded border border-neutral-alpha-4 object-contain ${className || ''}`}
          onError={onError}
          onLoad={onLoad}
        />
      )}
    </div>
  );
}

export default ImageWithFallback;
